import { useState } from 'react';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import {
  reactToPost,
  unreactFromPost,
  type CommentReactionGroup,
  type ReactionVocabItem,
} from '../../api/comments';
import styles from './PostReactionButton.module.css';

// =============================================================================
// PostReactionButton (DEV-720). The shared post-level react control used by the
// feed action bar AND the comment modal's action row, so reactions are
// identical everywhere: a heart + count that opens the emoji picker (the active
// reaction vocabulary). Toggling an emoji adds/removes the user's reaction.
// =============================================================================

interface PostReactionButtonProps {
  anchorType: string;
  anchorId: string;
  reactions: CommentReactionGroup[];
  vocab: ReactionVocabItem[];
  onChanged: () => void;
}

function formatCount(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(1).replace(/\.0$/, '')}K`;
  return String(n);
}

export function PostReactionButton({ anchorType, anchorId, reactions, vocab, onChanged }: PostReactionButtonProps) {
  const { t } = useAppTranslation('common');
  const [picking, setPicking] = useState(false);
  const [busy, setBusy] = useState(false);

  const total = reactions.reduce((s, r) => s + r.count, 0);
  const mine = reactions.some((r) => r.mineReacted);

  const toggle = async (reactionType: string) => {
    if (busy) return;
    setBusy(true);
    try {
      const has = reactions.find((r) => r.reactionType === reactionType)?.mineReacted;
      if (has) await unreactFromPost(anchorType, anchorId, reactionType);
      else await reactToPost(anchorType, anchorId, reactionType);
      setPicking(false);
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={styles.wrap}>
      <button
        type="button"
        className={`${styles.btn} ${mine ? styles.active : ''}`}
        disabled={busy}
        aria-label={t('comments.react')}
        onClick={() => setPicking((v) => !v)}
      >
        <i className="ti ti-heart" aria-hidden="true" />
        {total > 0 && <span>{formatCount(total)}</span>}
      </button>
      {picking && vocab.length > 0 && (
        <div className={styles.picker} role="menu">
          {vocab.map((v) => (
            <button
              key={v.reactionType}
              type="button"
              className={`${styles.pick} ${reactions.find((r) => r.reactionType === v.reactionType)?.mineReacted ? styles.pickActive : ''}`}
              onClick={() => toggle(v.reactionType)}
            >
              {v.reactionType}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default PostReactionButton;
